/**
 * comandaEscPos.ts — Builder ESC/POS de la comanda de cocina (sin precios).
 *
 * QUÉ HACE: Genera los bytes de la comanda que se envía a cocina al enviar una
 *   orden de mesa: negocio, «COMANDA», mesa, mesero, hora, ítems con cantidad
 *   y opciones, y cierre. Sin precios ni totales.
 * QUÉ EXPORTA: buildComandaEscPos, ComandaEscPosInput.
 * QUÉ NO DEBE HACER: No persiste, no conoce React ni envía bytes (eso lo hace
 *   `lib/native/posHardware.ts`).
 */
import {
  getTicketMaxWidth,
  loadBusinessForReceipt,
  sanitizeForThermal,
} from "@/features/caja/services/ticketRender.service";
import { formatSelectionsSummary } from "@/lib/formatLineSelections";
import type { ComandaMesa, OrdenMesa } from "../types/mesas.types";
import { etiquetaOrdenMesa } from "./ordenMesaHelpers";

const ESC = 0x1b;
const GS = 0x1d;

export interface ComandaEscPosInput {
  orden: OrdenMesa;
  /** Comanda puntual a imprimir; si falta se imprimen los ítems enviados. */
  comanda?: ComandaMesa;
  paperWidthMm?: 58 | 80;
}

const joinChunks = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
};

/** Parte el texto en líneas que caben en el ancho térmico. */
const wrapText = (text: string, width: number, indent = ""): string[] => {
  const clean = sanitizeForThermal(text).trim();
  if (!clean) return [];
  const words = clean.split(/\s+/);
  const lines: string[] = [];
  let current = indent;
  for (const word of words) {
    const candidate =
      current === indent ? `${indent}${word}` : `${current} ${word}`;
    if (candidate.length > width && current !== indent) {
      lines.push(current);
      current = `${indent}${word}`;
    } else {
      current = candidate;
    }
  }
  if (current !== indent) lines.push(current);
  return lines;
};

const formatHora = (iso: string): string =>
  new Date(iso).toLocaleString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export function buildComandaEscPos(input: ComandaEscPosInput): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const paper = input.paperWidthMm === 80 ? 80 : 58;
  const width = getTicketMaxWidth(paper);

  const add = (text: string) => {
    chunks.push(encoder.encode(sanitizeForThermal(text) + "\n"));
  };
  const addRaw = (bytes: number[]) => {
    chunks.push(new Uint8Array(bytes));
  };
  const separador = () => add("-".repeat(width));

  addRaw([ESC, 0x40]); // init
  addRaw([ESC, 0x61, 0x01]); // centrado

  const biz = loadBusinessForReceipt();
  if (biz.nombre) {
    addRaw([ESC, 0x45, 0x01]);
    add(biz.nombre.toUpperCase());
    addRaw([ESC, 0x45, 0x00]);
  }

  addRaw([ESC, 0x21, 0x30]); // doble ancho/alto
  add("COMANDA");
  addRaw([ESC, 0x21, 0x00]);

  addRaw([ESC, 0x21, 0x30]);
  addRaw([ESC, 0x45, 0x01]);
  add(etiquetaOrdenMesa(input.orden).toUpperCase());
  addRaw([ESC, 0x45, 0x00]);
  addRaw([ESC, 0x21, 0x00]);

  add(`Orden #${input.orden.numero} · ${input.orden.meseroNombre}`);
  add(formatHora(input.comanda?.enviadaEn ?? input.orden.updatedAt));
  if (input.comanda) add(`Comanda #${input.comanda.numero}`);

  addRaw([ESC, 0x61, 0x00]); // izquierda
  separador();

  const itemIds = input.comanda?.itemIds;
  const items = input.orden.items.filter(
    (item) => item.enviado && (!itemIds || itemIds.includes(item.cartItem.id)),
  );

  for (const item of items) {
    const cantidad = item.cartItem.peso
      ? `${item.cartItem.peso.toFixed(3)} kg`
      : `${Math.max(1, item.cartItem.quantity)}x`;
    const nombre = item.cartItem.nombre.toUpperCase();
    addRaw([ESC, 0x45, 0x01]);
    addRaw([ESC, 0x21, 0x10]); // doble alto
    for (const line of wrapText(`${cantidad}  ${nombre}`, width)) add(line);
    addRaw([ESC, 0x21, 0x00]);
    addRaw([ESC, 0x45, 0x00]);

    const selections = formatSelectionsSummary(item.cartItem);
    if (selections) {
      for (const line of wrapText(selections, width, "   ")) add(line);
    }
  }

  separador();
  addRaw([ESC, 0x61, 0x01]);
  add("*** COCINA ***");
  addRaw([ESC, 0x64, 0x03]); // feed 3
  addRaw([GS, 0x56, 0x42, 0x00]); // cut

  return joinChunks(chunks);
}
